import type { UploadFile } from 'antd/es/upload/interface';
import { get } from 'react-hook-form';

import type { MarketplaceUploadImageItem } from '../hooks/useMarketplaceImageUpload';
import type { ContentFormData, CreationFormData } from '../schemas/rootSchema';

type StaticImageFieldName =
  | 'creation.images.program.card'
  | 'creation.images.program.logo'
  | 'creation.images.program.vertical'
  | 'creation.images.combo.main'
  | 'creation.images.combo.search'
  | 'creation.images.combo.highlight.original'
  | 'creation.images.combo.highlight.small'
  | 'content.mainImage'
  | 'content.instructor.image';

interface ImageFieldValue {
  file?: unknown;
}

interface BuildMarketplaceImageUploadItemsInput {
  creation: CreationFormData;
  content: ContentFormData;
}

const STATIC_IMAGE_FIELD_CONFIG: Record<
  StaticImageFieldName,
  { uploadKey: string; label: string }
> = {
  'creation.images.program.card': {
    uploadKey: 'program.card',
    label: 'Imagem web (programa)',
  },
  'creation.images.program.logo': {
    uploadKey: 'program.logo',
    label: 'Logo (programa)',
  },
  'creation.images.program.vertical': {
    uploadKey: 'program.vertical',
    label: 'Imagem mobile (programa)',
  },
  'creation.images.combo.main': {
    uploadKey: 'combo.main',
    label: 'Imagem card principal (combo)',
  },
  'creation.images.combo.search': {
    uploadKey: 'combo.search',
    label: 'Imagem mobile/busca (combo)',
  },
  'creation.images.combo.highlight.original': {
    uploadKey: 'combo.highlight.original',
    label: 'Imagem combo programa web',
  },
  'creation.images.combo.highlight.small': {
    uploadKey: 'combo.highlight.small',
    label: 'Imagem combo programa mobile',
  },
  'content.mainImage': {
    uploadKey: 'landingPage.teaser',
    label: 'Imagem principal da landing page',
  },
  'content.instructor.image': {
    uploadKey: 'landingPage.program.instructor',
    label: 'Imagem do instrutor',
  },
};

const isFile = (value: unknown): value is File => value instanceof File;

export function buildMarketplaceImageUploadItems(
  data: BuildMarketplaceImageUploadItemsInput
): MarketplaceUploadImageItem[] {
  const uploadItems: MarketplaceUploadImageItem[] = [];

  const appendUploadItemByFieldName = (fieldName: StaticImageFieldName) => {
    const config = STATIC_IMAGE_FIELD_CONFIG[fieldName];
    const fieldValue = get(data, fieldName) as ImageFieldValue | undefined;

    if (!isFile(fieldValue?.file)) {
      return;
    }

    uploadItems.push({
      formFieldPath: fieldName,
      uploadKey: config.uploadKey,
      label: config.label,
      file: fieldValue.file,
    });
  };

  (Object.keys(STATIC_IMAGE_FIELD_CONFIG) as StaticImageFieldName[]).forEach(
    appendUploadItemByFieldName
  );

  const carouselImages = (data.content.programImages ?? []) as UploadFile[];

  carouselImages.forEach((image, index) => {
    if (!isFile(image.originFileObj)) {
      return;
    }

    uploadItems.push({
      formFieldPath: `content.programImages.${index}`,
      uploadKey: `landingPage.combo.whatYouGet.${index + 1}`,
      label: `Imagem do carrossel ${index + 1}`,
      file: image.originFileObj,
      arrayItemUid: image.uid,
    });
  });

  return uploadItems;
}
